import { useEffect, useSyncExternalStore } from 'react';
import { track } from './telemetry';
import { useQuery } from '@tanstack/react-query';
import { create } from 'zustand';
import type {
  PartyAction,
  PartyChatMessage,
  PartyEvent,
  PartyMedia,
  PartyReaction,
  PartyState,
} from '@teaplus/shared';
import { api, errorMessage, isApiError } from './api';
import { watchPathFor } from './watch';

type Listener = () => void;

export type PartyTitle = Pick<PartyMedia, 'type' | 'id' | 'season' | 'episode'>;

const ACTIVE_KEY = 'tp:active-party';

function readActive() {
  try {
    return sessionStorage.getItem(ACTIVE_KEY);
  } catch {
    return null;
  }
}

/** The party this window is in: it stays connected while you browse the menus. */
export const useActivePartyCode = create<{ code: string | null }>(() => ({ code: readActive() }));

/**
 * The page about to open another party says so while it draws, so the rest of the app moves with
 * it rather than reconnecting to the old party in the same moment.
 */
let claimed: string | null = null;
export function claimParty(code: string) {
  claimed = code;
}

export function setActiveParty(code: string | null) {
  const was = useActivePartyCode.getState().code;
  claimed = null;
  if (was === code) return;
  // Only one party at a time: going to another one leaves this one.
  if (was && code) {
    void api(`/api/party/${was}/action`, { body: { type: 'leave' }, keepalive: true }).catch(
      () => undefined,
    );
  }
  useActivePartyCode.setState({ code });
  if (code) usePartyDock.setState({ open: true });
  try {
    if (code) sessionStorage.setItem(ACTIVE_KEY, code);
    else sessionStorage.removeItem(ACTIVE_KEY);
  } catch {}
}

/** The party panel beside the page: open, or folded away behind the header's party button. */
export const usePartyDock = create<{ open: boolean }>(() => ({ open: true }));
export const setPartyDockOpen = (open: boolean) => usePartyDock.setState({ open });

/** Starts an empty party, or one with this title first in the queue. */
export async function startParty(first?: PartyTitle) {
  const p = await api<PartyState>('/api/party', { body: {} });
  if (first) await api(`/api/party/${p.code}/nominate`, { body: { ...first, startNow: false } });
  setActiveParty(p.code);
  return p.code;
}

export interface PartySnapshot {
  state: PartyState | null;
  error: string | null;
  messages: PartyChatMessage[];
  unread: number;
}

export class PartyConnection {
  private es: EventSource | null = null;
  private listeners = new Set<Listener>();
  private reactionListeners = new Set<(r: PartyReaction) => void>();
  private offset = 0;
  private closed = false;
  private retryTimer: number | null = null;
  chatOpen = false;
  snapshot: PartySnapshot = { state: null, error: null, messages: [], unread: 0 };

  constructor(readonly code: string) {}

  async open() {
    try {
      this.accept(await api<PartyState>(`/api/party/${this.code}/join`, { method: 'POST' }));
      await this.syncClock();
      this.connect();
    } catch (e) {
      this.fail(errorMessage(e));
    }
  }

  serverNow() {
    return Date.now() + this.offset;
  }

  async act(action: PartyAction) {
    try {
      this.accept(
        await api<PartyState>(`/api/party/${this.code}/action`, { body: action }),
        action.type === 'leave',
      );
    } catch (e) {
      if (isApiError(e, 'NOT_FOUND')) this.fail('This party has ended.');
      throw e;
    }
  }

  close() {
    this.closed = true;
    this.es?.close();
    this.es = null;
    if (this.retryTimer) window.clearTimeout(this.retryTimer);
  }

  async nominate(media: PartyTitle, startNow = false) {
    this.accept(
      await api<PartyState>(`/api/party/${this.code}/nominate`, { body: { ...media, startNow } }),
    );
  }

  subscribe = (l: Listener) => {
    this.listeners.add(l);
    return () => void this.listeners.delete(l);
  };

  onReaction(l: (r: PartyReaction) => void) {
    this.reactionListeners.add(l);
    return () => void this.reactionListeners.delete(l);
  }

  setChatOpen(open: boolean) {
    this.chatOpen = open;
    if (open && this.snapshot.unread) this.update({ unread: 0 });
  }

  getSnapshot = () => this.snapshot;

  private update(patch: Partial<PartySnapshot>) {
    this.snapshot = { ...this.snapshot, ...patch };
    this.listeners.forEach((l) => l());
  }

  private handle(e: PartyEvent) {
    if (e.type === 'state') return this.accept(e.state);
    if (e.type === 'history') return this.update({ messages: e.messages });
    if (e.type === 'reaction') return this.reactionListeners.forEach((l) => l(e.reaction));
    const mine = e.message.memberId !== null && e.message.memberId === this.snapshot.state?.you;
    this.update({
      messages: [...this.snapshot.messages, e.message].slice(-100),
      unread:
        this.chatOpen || mine || e.message.kind === 'system'
          ? this.snapshot.unread
          : this.snapshot.unread + 1,
    });
  }

  private connect() {
    if (this.closed) return;
    const es = new EventSource(`/api/party/${this.code}/events`);
    this.es = es;
    es.onmessage = (e) => {
      try {
        this.handle(JSON.parse(e.data) as PartyEvent);
      } catch {}
    };
    es.onerror = () => {
      if (this.closed || this.snapshot.state?.status === 'ended') return es.close();
      if (es.readyState === EventSource.CLOSED) {
        this.es = null;
        this.retryTimer = window.setTimeout(() => void this.recover(), 2000);
      }
    };
  }

  private async recover() {
    if (this.closed) return;
    try {
      this.accept(await api<PartyState>(`/api/party/${this.code}`));
      this.connect();
    } catch (e) {
      if (isApiError(e, 'NOT_FOUND')) this.fail('This party has ended.');
      else this.retryTimer = window.setTimeout(() => void this.recover(), 3000);
    }
  }

  private async syncClock() {
    let best = Infinity;
    for (let i = 0; i < 5; i++) {
      const t0 = Date.now();
      try {
        const { now } = await api<{ now: number }>('/api/party/time');
        const rtt = Date.now() - t0;
        if (rtt < best) {
          best = rtt;
          this.offset = now - (t0 + rtt / 2);
        }
      } catch {}
    }
  }

  private accept(s: PartyState, force = false) {
    const cur = this.snapshot.state;
    if (!force && cur && s.version < cur.version && s.status !== 'ended') return;
    this.update({ state: s, error: null });
    if (s.status === 'ended') this.es?.close();
  }

  private fail(message: string) {
    this.update({ error: message });
    this.es?.close();
  }
}

let current: PartyConnection | null = null;

export function connectParty(code: string) {
  if (current?.code === code) return current;
  current?.close();
  current = new PartyConnection(code);
  void current.open();
  return current;
}

export async function leaveParty(code?: string) {
  if (code && current?.code !== code) return;
  const c = current;
  current = null;
  setActiveParty(null);
  if (!c) return;
  try {
    if (c.snapshot.state?.status !== 'ended') await c.act({ type: 'leave' });
  } catch {}
  c.close();
}

/** Leaving while the window closes: keepalive, so the request outlives the page. */
export function leavePartyOnUnload(code: string) {
  if (current?.code !== code || current.snapshot.state?.status === 'ended') return;
  void api(`/api/party/${code}/action`, { body: { type: 'leave' }, keepalive: true }).catch(
    () => undefined,
  );
}

export function forgetParty(code: string) {
  if (useActivePartyCode.getState().code === code) setActiveParty(null);
  if (current?.code !== code) return;
  current.close();
  current = null;
}

/** Closing the player mid-film: still in the party, just not watching this one. */
export function sitOutOfParty(code: string) {
  if (current?.code !== code || current.snapshot.state?.status === 'ended') return;
  void current.act({ type: 'sitOut', out: true }).catch(() => undefined);
}

const EMPTY: PartySnapshot = { state: null, error: null, messages: [], unread: 0 };

let users = 0;
let idleTimer: number | null = null;

function usePartyUser(active: boolean) {
  useEffect(() => {
    if (!active) return;
    users++;
    if (idleTimer) window.clearTimeout(idleTimer);
    idleTimer = null;
    return () => {
      users--;
      if (users > 0) return;
      idleTimer = window.setTimeout(() => {
        idleTimer = null;
        if (users > 0 || !current) return;
        current.close();
        current = null;
      }, 4000);
    };
  }, [active]);
}

export function useParty(code: string | null) {
  usePartyUser(!!code);
  const conn = code ? connectParty(code) : null;
  const snap = useSyncExternalStore(
    conn?.subscribe ?? noopSubscribe,
    conn?.getSnapshot ?? (() => EMPTY),
  );
  const me = snap.state?.members.find((m) => m.id === snap.state?.you) ?? null;
  return { conn, ...snap, me, isHost: !!me?.isHost };
}

const noopSubscribe = () => () => undefined;

/** The party you're in right now, wherever you are in the app. */
export function useActiveParty() {
  const enabled = usePartyEnabled();
  const code = useActivePartyCode((s) => s.code);
  return useParty(enabled ? (claimed ?? code) : null);
}

/** Who else in your party is looking at this title. Cheap enough for every card on a page. */
export function usePartyLookers(mediaId: string) {
  const code = useActivePartyCode((s) => s.code);
  const conn = code && current?.code === code ? current : null;
  const { state } = useSyncExternalStore(
    conn?.subscribe ?? noopSubscribe,
    conn?.getSnapshot ?? (() => EMPTY),
  );
  if (!state) return [];
  return state.members.filter((m) => m.id !== state.you && m.activity?.media?.id === mediaId);
}

export function partyWatchPath(m: PartyMedia, code: string) {
  return `${watchPathFor(m.type, m.id, m.season ?? undefined, m.episode ?? undefined)}?party=${code}`;
}

export function useLinks() {
  const q = useQuery({
    queryKey: ['system', 'links'],
    queryFn: () =>
      api<{
        linkUrl: string;
        downloadUrl: string;
        addonsUrl: string;
        webAppUrl: string | null;
        features: { party: boolean };
      }>('/api/system/links'),
    staleTime: Infinity,
  });
  return (
    q.data ?? {
      linkUrl: 'https://app.teaplus.tv',
      downloadUrl: '',
      addonsUrl: 'https://addons.teaplus.tv',
      webAppUrl: null,
      features: { party: !!window.teaplus?.desktop },
    }
  );
}

/** Watch Together is a desktop feature: the server says no on the web app (web.teaplus.tv). */
export const usePartyEnabled = () => useLinks().features.party;

export function useMembersToasts(state: PartyState | null, notify: (text: string) => void) {
  useEffect(() => {
    if (!state) return;
    const key = `tp:party-members:${state.code}`;
    const names = new Map(state.members.map((m) => [m.id, m.name]));
    let before: [string, string][] | null = null;
    try {
      before = JSON.parse(sessionStorage.getItem(key) ?? 'null');
    } catch {}
    if (before) {
      const prev = new Map(before);
      for (const [id, name] of names)
        if (!prev.has(id) && id !== state.you) notify(`${name} joined`);
      for (const [id, name] of prev) if (!names.has(id)) notify(`${name} left`);
    }
    try {
      sessionStorage.setItem(key, JSON.stringify([...names]));
    } catch {}
  }, [state, notify]);
}

/** Copies to the clipboard. `what` (a share link, a friend code) is noted for the owner's stats. */
export async function copyText(text: string, what?: string, mediaId?: string) {
  try {
    await navigator.clipboard.writeText(text);
    if (what) track({ kind: 'link_copied', where: what, mediaId });
    return true;
  } catch {
    return false;
  }
}
